import { useEffect, useMemo } from 'react';

import { FlexColumn } from '@editor/components/utilities/layouts';
import { MinimumWorkspace, useWorkspacesForUserQuery } from '@repo/api-client';
import {
  subscriptionStoreSelector,
  useSubscriptionStore,
  useSubscriptionStoreSync,
} from '@repo/subscription/client';
import { Avatar } from '@repo/ui-kit/components/core/Avatars';
import { Button } from '@repo/ui-kit/components/core/Button';
import { Dialog } from '@repo/ui-kit/components/core/Dialog';
import { H4, H6, H7 } from '@repo/ui-kit/components/core/Typography';

import { useWorkspaceStore } from 'stores/workspaceStore';

import {
  DialogTitle,
  WorkspaceList,
  WorkspaceItem,
  WorkspaceItemDetails,
  WorkspaceInfo,
  MemberCountWrapper,
} from './styles';

/**
 * This component is used to select a workspace to upgrade
 * from the user's list of workspaces. Only FREE workspaces are shown.
 *
 * On selection, the user is redirected to the target workspace, and
 * the upgrade modal is shown after the store is updated.
 */
export const SelectWorkspaceUpgrade: React.FC<{
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  selectedWorkspaceId: string | null;
  setSelectedWorkspaceId: (id: string | null) => void;
  onWorkspaceChange?: (workspace: MinimumWorkspace) => void;
}> = ({
  isOpen,
  setIsOpen,
  selectedWorkspaceId,
  setSelectedWorkspaceId,
  onWorkspaceChange,
}) => {
  const { data: workspaces, isLoading: isLoadingWorkspaces } =
    useWorkspacesForUserQuery();

  // Get the active workspace ID from the store
  const activeWorkspaceId = useWorkspaceStore(
    (state) => state.activeWorkspaceId
  );

  // Get showUpgradeModal from the subscription store
  const showUpgradeModal = useSubscriptionStore(
    subscriptionStoreSelector.showUpgradeModal
  );

  // Check if current and active workspace match
  const targetWorkspaceId =
    selectedWorkspaceId === activeWorkspaceId ? selectedWorkspaceId : null;

  // Use the hook to check if subscription store is ready for the target workspace
  const isStoreReady = useSubscriptionStoreSync(targetWorkspaceId);

  // Watch for store sync state and trigger upgrade when ready
  useEffect(() => {
    if (!selectedWorkspaceId || !activeWorkspaceId) {
      return;
    }

    // If store is ready for the target workspace, trigger upgrade
    if (isStoreReady) {
      showUpgradeModal('noIncentiveUpgrade', {
        trackingLabel: 'freeLimitReached',
      });
      setIsOpen(false);
      setSelectedWorkspaceId(null);
    }
  }, [
    activeWorkspaceId,
    selectedWorkspaceId,
    isStoreReady,
    showUpgradeModal,
    setIsOpen,
    setSelectedWorkspaceId,
  ]);

  // Filter workspaces - only show FREE workspaces that the user owns
  const filteredWorkspaces = useMemo(() => {
    if (!workspaces) {
      return [];
    }

    return workspaces.filter(
      (workspace) =>
        workspace && workspace.plan === 'FREE' && workspace.isWorkspaceOwner
    );
  }, [workspaces]);

  const handleSelectWorkspace = (workspace: MinimumWorkspace) => {
    setSelectedWorkspaceId(workspace.id);
    if (onWorkspaceChange) {
      onWorkspaceChange(workspace);
    }
  };

  const title = 'Select a team to upgrade';
  const buttonText = 'Upgrade';

  return (
    <Dialog.Root modal={true} open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Content
        height="auto"
        showCloseButton={true}
        title={title}
        width="312px"
      >
        <FlexColumn gap="16px">
          <DialogTitle>
            <H4 bold>{title}</H4>
          </DialogTitle>

          <WorkspaceList>
            <H6 color="secondary" margin="0 0 8px 0">
              You’ve reached the limit of free teams. Upgrade free teams to
              create a new one.
            </H6>

            {isLoadingWorkspaces ? (
              <FlexColumn alignItems="center" padding="16px 0">
                <H4>Loading teams...</H4>
              </FlexColumn>
            ) : filteredWorkspaces.length > 0 ? (
              <FlexColumn>
                {filteredWorkspaces.map(
                  (workspace) =>
                    workspace && (
                      <WorkspaceItem key={workspace.id}>
                        <WorkspaceItemDetails>
                          <Avatar
                            fallback={workspace.name.charAt(0)}
                            round="square"
                          />
                          <WorkspaceInfo>
                            <H6 bold textOverflowEllipsis>
                              {workspace.name}
                            </H6>
                            <MemberCountWrapper>
                              <H7 color="secondary">
                                {workspace.memberCount} members
                              </H7>
                            </MemberCountWrapper>
                          </WorkspaceInfo>
                        </WorkspaceItemDetails>

                        <Button
                          name="selectWorkspace"
                          size="m"
                          variant="secondary"
                          onClick={() => handleSelectWorkspace(workspace)}
                        >
                          <Button.Label>{buttonText}</Button.Label>
                        </Button>
                      </WorkspaceItem>
                    )
                )}
              </FlexColumn>
            ) : (
              <FlexColumn alignItems="center" padding="16px 0">
                <H4>No eligible teams found. Please reload.</H4>
              </FlexColumn>
            )}
          </WorkspaceList>
        </FlexColumn>
      </Dialog.Content>
    </Dialog.Root>
  );
};
